import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery, Box, CssBaseline, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useUser } from "@clerk/clerk-react";
import { ScrollProvider } from "./contexts/ScrollContext";
import { useBadgeUpdater } from "./hooks/useBadgeUpdater";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useNetworkStatus } from "./hooks/useNetworkStatus";
import { useAuthSync, SyncStatus } from "./hooks/useAuthSync";
import { useSignOut } from "./hooks/useSignOut";
import { useClerkReduxSync } from "./hooks/useClerkReduxSync";
import { savePushSubscription } from "./api/notificationAPI";
import { useSockets } from "./contexts/SocketContext";
import { isProfileComplete } from "./utils/profileValidation";
import { useAuthStatus, AuthStatus } from "./hooks/useAuthStatus";

import AppRoutes from "./routes/AppRoutes";
import Layout from "./components/Layout";
import LeftMenu from "./components/LeftMenu";
import LoadingSpinner from "./components/LoadingSpinner";
import AuthLoadingScreen from "./components/AuthLoadingScreen";
import OfflineBanner from "./components/OfflineBanner";
import StatusUpdatePanel from "./components/StatusUpdatePanel";
import CheckInPromptModal from "./components/CheckInPromptModal";
import { ActiveChannelProvider } from "./contexts/ActiveChannelContext";
import "./App.css";

const LazyChatProvider = lazy(
  () => import("./components/Chat/LazyChatProvider"),
);

const StatusPanelWrapper = () => {
  const { statusUpdates, showStatusPanel, setShowStatusPanel } = useSockets();

  return (
    <StatusUpdatePanel
      visible={showStatusPanel}
      updates={statusUpdates}
      onDismiss={() => {
        setShowStatusPanel(false);
        setTimeout(() => {
          // Clear updates after animation
        }, 300);
      }}
    />
  );
};

const CheckInPromptWrapper = () => {
  const { checkInPrompt, dismissCheckInPrompt, markAsCheckedIn } = useSockets();
  const { user } = useSelector((state) => state.loggedUser);
  const [currentLocation, setCurrentLocation] = useState(null);

  // Get fresh location when prompt opens
  useEffect(() => {
    if (checkInPrompt && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Failed to get location for check-in:", error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
      );
    }
  }, [checkInPrompt]);

  if (!checkInPrompt) return null;

  // Use fresh geolocation first, then fall back to user's stored location
  const userLat = currentLocation?.lat ?? user?.latitude;
  const userLng = currentLocation?.lng ?? user?.longitude;

  // Callback when check-in succeeds
  const handleCheckInSuccess = (requestId) => {
    markAsCheckedIn(requestId);
    // Note: Partner notification is handled by the backend via notification service
    // No need to emit socket event here to avoid double notifications
  };

  return (
    <CheckInPromptModal
      open={!!checkInPrompt}
      onClose={dismissCheckInPrompt}
      linkupRequestId={checkInPrompt.linkup_request_id}
      linkupId={checkInPrompt.linkup_id}
      linkupTitle={checkInPrompt.linkup_title}
      linkupActivity={checkInPrompt.linkup_activity}
      linkupDate={checkInPrompt.linkup_date}
      partnerName={checkInPrompt.partner_name}
      partnerAvatar={checkInPrompt.partner_avatar}
      venueName={checkInPrompt.venue_name}
      venueAddress={checkInPrompt.venue_address}
      distanceToVenue={checkInPrompt.distance_to_venue}
      distanceToPartner={checkInPrompt.distance_to_partner}
      partnerId={checkInPrompt.partner_id}
      userId={user?.id}
      userLat={userLat}
      userLng={userLng}
      onCheckInSuccess={handleCheckInSuccess}
    />
  );
};

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const BadgeUpdaterWrapper = ({ user, children, chatClient }) => {
  const updateBadges = useBadgeUpdater();
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const unreadNotificationsCount = useSelector(
    (state) => state.notifications?.unreadCount || 0,
  );

  useEffect(() => {
    const initPush = async () => {
      if (
        !user?.id ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window)
      )
        return;
      try {
        const registration = await navigator.serviceWorker.ready;
        let permission = Notification.permission;
        if (permission === "default")
          permission = await Notification.requestPermission();
        if (permission !== "granted") return;
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(
              process.env.REACT_APP_VAPID_PUBLIC_KEY,
            ),
          });
        }
        if (subscription) {
          const subString = JSON.stringify(subscription);
          const cacheKey = `push_sub_synced_${user.id}`;
          if (localStorage.getItem(cacheKey) !== subString) {
            const response = await savePushSubscription(user.id, subscription);
            if (response?.success) localStorage.setItem(cacheKey, subString);
          }
        }
      } catch (error) {
        console.error("[Push Init] Failed:", error);
      }
    };
    initPush();
  }, [user?.id]);

  const hasUpdatedBadges = useRef(false);
  useEffect(() => {
    if (user?.id && !hasUpdatedBadges.current) {
      hasUpdatedBadges.current = true;
      updateBadges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!chatClient) return;
    const handleEvent = (event) => {
      if (event.total_unread_count !== undefined)
        setUnreadChatCount(event.total_unread_count);
    };
    chatClient.on(handleEvent);
    return () => chatClient.off(handleEvent);
  }, [chatClient]);

  useEffect(() => {
    const totalCount = unreadChatCount + unreadNotificationsCount;
    const syncBadge = async () => {
      const countToSet =
        document.visibilityState === "visible" ? 0 : totalCount;
      if ("setAppBadge" in navigator) {
        try {
          if (countToSet > 0) await navigator.setAppBadge(countToSet);
          else await navigator.clearAppBadge();
        } catch (e) {}
      }
      if ("serviceWorker" in navigator) {
        try {
          const reg = await navigator.serviceWorker.ready;
          if (reg.active)
            reg.active.postMessage({
              type: countToSet > 0 ? "UPDATE_BADGE" : "CLEAR_BADGE",
              count: countToSet,
            });
        } catch (e) {}
      }
    };
    syncBadge();
    window.addEventListener("focus", syncBadge);
    document.addEventListener("visibilitychange", syncBadge);
    return () => {
      window.removeEventListener("focus", syncBadge);
      document.removeEventListener("visibilitychange", syncBadge);
    };
  }, [unreadChatCount, unreadNotificationsCount]);

  return <>{children}</>;
};

const App = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useSelector((state) => state.loggedUser);
  const { user: clerkUser, isSignedIn, isLoaded } = useUser();
  const [chatClient, setChatClient] = useState(null);
  const [clientReady, setClientReady] = useState(false);
  const [chatConnectionFailed, setChatConnectionFailed] = useState(false);
  const internalSignOut = useSignOut();

  // Single source of truth for authentication status
  const { status: authStatus } = useAuthStatus();

  useOnlineStatus(user?.id, chatClient);
  useNetworkStatus();
  useClerkReduxSync();
  const { authError, syncStatus, isNewUser } = useAuthSync(
    clerkUser,
    isSignedIn,
    isLoaded,
  );

  // Dynamic viewport height (iOS keyboard safe)

  // Global tap top to scroll-to-top handler
  useEffect(() => {
    // Only enable on mobile
    if (window.innerWidth >= 640) return;
    const handleTapTop = (e) => {
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      if (y <= 30) {
        const selectors = [
          "main",
          ".virtual-scroll-container",
          ".feed-scroll-container",
          "[data-scroll-container]",
          ".scroll",
          ".page-scroll-container",
          ".legal-scroll-container",
          "html",
          "body",
        ];
        selectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((el) => {
            if (el.scrollTop > 0) {
              el.scrollTo({ top: 0, behavior: "smooth" });
            }
          });
        });
      }
    };
    window.addEventListener("touchstart", handleTapTop);
    window.addEventListener("click", handleTapTop);
    return () => {
      window.removeEventListener("touchstart", handleTapTop);
      window.removeEventListener("click", handleTapTop);
    };
  }, []);
  useEffect(() => {
    const setAppHeight = () => {
      document.documentElement.style.setProperty(
        "--app-height",
        `${window.innerHeight}px`,
      );
    };
    setAppHeight();
    window.addEventListener("resize", setAppHeight);
    return () => window.removeEventListener("resize", setAppHeight);
  }, []);

  useEffect(() => {
    // Only connect to chat for users with complete AND VALID profiles
    // Users still in registration or with invalid data should not connect yet
    const profileComplete = isProfileComplete(user);
    if (
      !user?.id ||
      !user.access_token ||
      !profileComplete ||
      clientReady ||
      chatConnectionFailed
    )
      return;
    let isMounted = true;
    let timeoutId = null;

    // Timeout fallback: if chat doesn't connect in 10 seconds, proceed without it
    // This handles cases where Brave or other privacy browsers block Stream.io
    timeoutId = setTimeout(() => {
      if (isMounted && !clientReady) {
        console.warn(
          "[Chat] Connection timeout - proceeding without chat. This may be due to browser privacy settings.",
        );
        setChatConnectionFailed(true);
        setClientReady(true); // Allow app to proceed
      }
    }, 10000);

    import("stream-chat")
      .then((module) => {
        if (!isMounted) return;
        const client = module.StreamChat.getInstance(
          process.env.REACT_APP_STREAM_API_KEY,
        );
        return client
          .connectUser(
            { id: user.id, name: user.name, image: user.avatar },
            user.access_token,
          )
          .then(() => {
            if (isMounted) {
              clearTimeout(timeoutId);
              setChatClient(client);
              setClientReady(true);
            }
          });
      })
      .catch((error) => {
        console.error("[Chat] Connection failed:", error);
        if (isMounted) {
          clearTimeout(timeoutId);
          setChatConnectionFailed(true);
          setClientReady(true); // Allow app to proceed without chat
        }
      });

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // Include user fields used in connectUser to avoid stale data
  }, [
    user?.id,
    user?.name,
    user?.access_token,
    user?.gender,
    user?.date_of_birth,
    user?.avatar,
    clientReady,
    chatConnectionFailed,
  ]);

  // Track if we've ever had a signed-in user this session
  const hadUserRef = useRef(false);
  useEffect(() => {
    if (clerkUser) hadUserRef.current = true;
  }, [clerkUser]);

  useEffect(() => {
    const handleSignOutCleanup = async () => {
      // Only clean up if we HAD a user and now they're gone (actual sign-out or deletion)
      // Don't trigger for users who were never signed in (public pages)
      if (isLoaded && !clerkUser && hadUserRef.current) {
        if (chatClient) {
          await chatClient.disconnectUser();
          setChatClient(null);
        }
        setClientReady(false);
        setChatConnectionFailed(false);
        sessionStorage.setItem("signingOut", "true");
        await internalSignOut();
        hadUserRef.current = false; // Reset after cleanup
      }
    };
    handleSignOutCleanup();
  }, [clerkUser, isLoaded, chatClient, internalSignOut]);

  useEffect(() => {
    if (sessionStorage.getItem("signingOut") === "true") {
      sessionStorage.removeItem("signingOut");
    }
  }, []);

  // Logical Readiness Gate - simplified using authStatus
  // Users needing registration don't need chat, so they're "ready" sooner
  const isAppReady =
    isLoaded &&
    (authStatus === AuthStatus.UNAUTHENTICATED ||
      authStatus === AuthStatus.NEEDS_REGISTRATION ||
      (authStatus === AuthStatus.AUTHENTICATED && clientReady));

  /**
   * Simplified render logic using single source of truth (authStatus)
   *
   * States:
   * - LOADING: Show spinner (waiting for Clerk/backend sync)
   * - UNAUTHENTICATED: Show public routes (landing page)
   * - NEEDS_REGISTRATION: Show registration (no chat required)
   * - AUTHENTICATED: Show full app with chat
   */
  const renderInnerContent = () => {
    switch (authStatus) {
      case AuthStatus.LOADING:
        // Show optimistic UI during OAuth signup when syncing with backend
        const isSyncing =
          syncStatus === SyncStatus.CONNECTING ||
          syncStatus === SyncStatus.SYNCING;
        if (authError) {
          return (
            <Box
              sx={{
                flex: 1,
                height: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#fff",
                px: 3,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 420,
                  textAlign: "center",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 4,
                  p: 4,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.06)",
                }}
              >
                <Box
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827",
                    mb: 1,
                  }}
                >
                  We couldn’t finish setting up your account.
                </Box>
                <Box sx={{ color: "#6b7280", mb: 3, lineHeight: 1.6 }}>
                  Your sign-in is active, but the profile sync did not complete.
                  Tap retry to try again.
                </Box>
                <Button
                  variant="contained"
                  onClick={() => window.location.reload()}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: "#0071e3",
                    "&:hover": { bgcolor: "#0077ed" },
                  }}
                >
                  Retry
                </Button>
              </Box>
            </Box>
          );
        }
        if (isSyncing && isSignedIn) {
          return (
            <AuthLoadingScreen syncStatus={syncStatus} isNewUser={isNewUser} />
          );
        }
        // Default loading state (Clerk still loading, etc.)
        return (
          <Box
            sx={{
              flex: 1,
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#fff",
            }}
          >
            <LoadingSpinner />
          </Box>
        );

      case AuthStatus.UNAUTHENTICATED:
        return <AppRoutes />;

      case AuthStatus.NEEDS_REGISTRATION:
        // Users in registration flow - render routes without chat
        return <AppRoutes chatClient={null} />;

      case AuthStatus.AUTHENTICATED:
        // Fully authenticated users - wait for chat connection
        if (!clientReady) {
          return (
            <Box
              sx={{
                flex: 1,
                height: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#fff",
              }}
            >
              <LoadingSpinner />
            </Box>
          );
        }

        // Chat failed or not available - render without chat provider
        if (chatConnectionFailed || !chatClient) {
          return (
            <Box sx={{ display: "flex", flex: 1, width: "100%" }}>
              <LeftMenu isMobile={isMobile} chatClient={null} />
              <AppRoutes chatClient={null} />
            </Box>
          );
        }

        // Full app with chat
        return (
          <Suspense
            fallback={
              <Box sx={{ flex: 1, bgcolor: "#fff" }}>
                <LoadingSpinner />
              </Box>
            }
          >
            <LazyChatProvider chatClient={chatClient}>
              <BadgeUpdaterWrapper user={user} chatClient={chatClient}>
                <Box sx={{ display: "flex", flex: 1, width: "100%" }}>
                  <LeftMenu isMobile={isMobile} chatClient={chatClient} />
                  <AppRoutes chatClient={chatClient} />
                </Box>
              </BadgeUpdaterWrapper>
            </LazyChatProvider>
          </Suspense>
        );

      default:
        return <AppRoutes />;
    }
  };

  return (
    <>
      <CssBaseline />
      <ScrollProvider>
        <ActiveChannelProvider>
          {/* Persistent Layout Wrapper */}
          <Layout isMobile={isMobile} isAppReady={isAppReady}>
            {renderInnerContent()}
          </Layout>
          <CheckInPromptWrapper />
        </ActiveChannelProvider>
      </ScrollProvider>
      <StatusPanelWrapper />
      <OfflineBanner />
    </>
  );
};

export default App;
